/**
 * A `Result` type represents either a success or a failure. It is an
 * alternative to throwing errors with the advantage of having typed errors.
 *
 * You can construct result values with `Result.failure` and `Result.success`
 * static methods.
 */
export type Result<A, E = never> =
	| Failure<A, E>
	| Success<A, E>
	;

export const Result = {
	try<A, E = unknown>(f: () => A): Result<A, E> {
		try {
			return Result.success(f());
		} catch (error) {
			return Result.failure(error as E);
		}
	},

	tryCatch<A, E>(options: {
		try: () => A,
		catch: (error: unknown) => E,
	}): Result<A, E> {
		try {
			return Result.success(options.try());
		} catch (error) {
			return Result.failure(options.catch(error));
		}
	},

	tryPromise<R, L = unknown>(promise: Promise<R>): Promise<Result<R, L>> {
		return promise.then(Result.success, Result.failure as (failure: unknown) => Result<never, L>);
	},

	async mapAsync<A, E, A2>(self: Promise<Result<A, E>>, f: (success: A) => Promise<A2>): Promise<Result<A2, E>> {
		const either = await self;
		switch (either._tag) {
			case "Failure": return either as Failure<never, E>;
			case "Success": return Result.success(await f(either.success));
		}
	},

	async flatMapAsync<A, E, A2, E2>(self: Promise<Result<A, E>>, f: (success: A) => Promise<Result<A2, E2>>): Promise<Result<A2, E | E2>> {
		const either = await self;
		switch (either._tag) {
			case "Failure": return either as Failure<never, E>;
			case "Success": return await f(either.success);
		}
	},

	failure<L>(failure: L): Result<never, L> {
		const a = Object.create(FailureProto);
		a.failure = failure;
		return a;
	},

	success<R>(success: R): Result<R> {
		const a = Object.create(SuccessProto);
		a.success = success;
		return a;
	},

	isResult(input: unknown): input is Result<unknown, unknown> {
		return typeof input === "object" && input !== null && TypeId in input;
	},
};

interface ResultBase<out A, out E> {
	readonly [TypeId]: {
		readonly _A: (_: never) => A,
		readonly _E: (_: never) => E,
	};

	isFailure(this: Result<A, E>): this is Failure<A, E>;
	isSuccess(this: Result<A, E>): this is Success<A, E>;
	map<A2>(this: Result<A, E>, f: (success: A) => A2): Result<A2, E>;
	mapAsync<A2>(this: Result<A, E>, f: (success: A) => Promise<A2>): Promise<Result<A2, E>>;
	flatMap<A2, E2>(this: Result<A, E>, f: (success: A) => Result<E2, A2>): Result<E2, E | A2>;
	flatMapAsync<A2, E2>(this: Result<A, E>, f: (success: A) => Promise<Result<E2, A2>>): Promise<Result<E2, E | A2>>;
	match<A2, E2>(this: Result<A, E>, options: {
		readonly onFailure: (failure: E) => E2,
		readonly onSuccess: (success: A) => A2,
	}): A2 | E2;
	getOrElse<A2>(this: Result<A, E>, onFailure: (failure: E) => A2): A | A2;
	getOrNull(this: Result<A, E>): A | null;
	getOrUndefined(this: Result<A, E>): A | undefined;
	getOrThrowWith(this: Result<A, E>, onFailure: (failure: E) => unknown): A;
}

export interface Failure<out A, out E> extends ResultBase<A, E> {
	readonly _tag: "Failure";
	readonly failure: E;
}

export interface Success<out A, out E> extends ResultBase<A, E> {
	readonly _tag: "Success";
	readonly success: A;
}

const TypeId = Symbol.for("Result");
type TypeId = typeof TypeId;

const CommonProto = {
	[TypeId]: {
		_A: (_: never) => _,
		_E: (_: never) => _,
	},

	isFailure<A, E>(this: Result<A, E>) {
		return this._tag === "Failure";
	},

	isSuccess<A, E>(this: Result<A, E>) {
		return this._tag === "Success";
	},

	map<A, E, A2>(this: Result<A, E>, f: (success: A) => A2) {
		switch (this._tag) {
			case "Failure": return this;
			case "Success": return Result.success(f(this.success));
		}
	},

	mapAsync<A, E, A2>(this: Result<A, E>, f: (success: A) => Promise<A2>) {
		switch (this._tag) {
			case "Failure": return Promise.resolve(this);
			case "Success": return f(this.success).then(Result.success);
		}
	},

	flatMap<A, E, A2, E2>(this: Result<A, E>, f: (success: A) => Result<E2, A2>) {
		switch (this._tag) {
			case "Failure": return this;
			case "Success": return f(this.success);
		}
	},

	flatMapAsync<A, E, A2, E2>(this: Result<A, E>, f: (success: A) => Promise<Result<E2, A2>>) {
		switch (this._tag) {
			case "Failure": return Promise.resolve(this);
			case "Success": return f(this.success);
		}
	},

	match<A, E, A2, E2>(this: Result<A, E>, options: {
		readonly onFailure: (failure: E) => E2,
		readonly onSuccess: (success: A) => A2,
	}) {
		switch (this._tag) {
			case "Failure": return options.onFailure(this.failure);
			case "Success": return options.onSuccess(this.success);
		}
	},

	getOrElse<A, E, A2>(this: Result<A, E>, onFailure: (failure: E) => A2) {
		switch (this._tag) {
			case "Failure": return onFailure(this.failure);
			case "Success": return this.success;
		}
	},

	getOrNull<A, E>(this: Result<A, E>) {
		switch (this._tag) {
			case "Failure": return null;
			case "Success": return this.success;
		}
	},

	getOrUndefined<A, E>(this: Result<A, E>) {
		switch (this._tag) {
			case "Failure": return undefined;
			case "Success": return this.success;
		}
	},

	getOrThrowWith<A, E>(this: Result<A, E>, onFailure: (failure: E) => unknown) {
		switch (this._tag) {
			case "Failure": throw onFailure(this.failure);
			case "Success": return this.success;
		}
	},
};

const SuccessProto = Object.assign(Object.create(CommonProto), {
	_tag: "Success",
});

const FailureProto = Object.assign(Object.create(CommonProto), {
	_tag: "Failure",
});
